import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import { registerPlayer } from "../../functions/registerPlayer";
import { getPlayer } from "../../functions/getPlayer";
import { submitGameResult } from "../../functions/submitGameResult";
import { sendReport } from "../../functions/sendReport";

const MOBILE_BREAKPOINT = 768;
const GAME_COMPLETE_MESSAGE_TYPE = "spacegame:gameComplete";

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") || sessionStorage.getItem("userData") || "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => window.innerWidth <= MOBILE_BREAKPOINT
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
}

export default function SpaceGame() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);
  const userData = useMemo(() => readStoredUserData(), []);
  const isMobile = useIsMobile();
  const iframeRef = useRef(null);
  const gameCompleteHandledRef = useRef(false);
  const [replayChecked, setReplayChecked] = useState(false);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const teamId = queryParams.get("teamId") || "";
  const source =
    queryParams.get("source") || process.env.REACT_APP_GAME_ENVIROMENT || "local";

  const userId =
    queryParams.get("userId") || String(user?.userId ?? userData?.userId ?? "").trim();
  const playerName = String(user?.name ?? userData?.name ?? "").trim();
  const sessionId =
    queryParams.get("sessionId") || String(user?.sessionId ?? userData?.sessionId ?? "").trim();
  const organizationId =
    queryParams.get("organizationId") ||
    String(user?.organizationId ?? userData?.organizationId ?? "").trim();

  useEffect(() => {
    if (!userId || !sessionId || !organizationId) return undefined;
    let cancelled = false;
    getPlayer({ userId, sessionId, organizationId })
      .then((res) => {
        if (cancelled) return;
        if (res.status === "completed") {
          navigate("/thankyou", { replace: true });
          return;
        }
        setReplayChecked(true);
      })
      .catch(() => {
        // No existing player row yet — safe to proceed.
        if (!cancelled) setReplayChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, sessionId, organizationId, navigate]);

  useEffect(() => {
    if (!userId || !playerName || !adminToken) return;
    registerPlayer({ userId, playerName, token: adminToken }).catch((err) => {
      console.error("Failed to register player for Space Game:", err);
    });
  }, [userId, playerName, adminToken]);

  useEffect(() => {
    if (!userId || !adminToken) return undefined;

    const unityBase =
      process.env.REACT_APP_UNITY_URL ||
      "https://2023-extramileplay-php.s3.amazonaws.com/UAT/spacegame/index.html";
    let unityOrigin;
    try {
      unityOrigin = new URL(unityBase).origin;
    } catch {
      unityOrigin = null;
    }

    const handleMessage = async (event) => {
      if (unityOrigin && event.origin !== unityOrigin) return;
      if (iframeRef.current && event.source !== iframeRef.current.contentWindow) return;
      if (event.data?.type !== GAME_COMPLETE_MESSAGE_TYPE) return;
      if (gameCompleteHandledRef.current) return;
      gameCompleteHandledRef.current = true;

      const points = Number(event.data.points) || 0;
      const timeSeconds = Number(event.data.time) || 0;

      try {
        const result = await submitGameResult({
          userId,
          points,
          time: timeSeconds,
          token: adminToken,
        });

        try {
          await sendReport({
            sessionId: user?.sessionId || sessionId,
            role: user?.role || "",
            token: user?.token || null,
            name: user?.name || playerName,
            userId,
            gameId: user?.gameId || "",
            organizationId: user?.organizationId || organizationId,
            points: result.points,
            time: result.time,
            reportId: user?.reportId || null,
            ans: "",
          });
        } catch (reportErr) {
          console.error("Failed to send report to extramileplay API:", reportErr);
        }
      } catch (err) {
        console.error("Failed to submit game result:", err);
      } finally {
        window.setTimeout(() => navigate("/thankyou"), 5000);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [userId, adminToken, sessionId, organizationId, playerName, user, navigate]);

  const handleFullscreenClick = () => {
    const el = iframeRef.current;
    if (!el) return;
    const request =
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.mozRequestFullScreen ||
      el.msRequestFullscreen;
    if (request) request.call(el);
  };

  const unityUrl = useMemo(() => {
    const base =
      process.env.REACT_APP_UNITY_URL ||
      "https://2023-extramileplay-php.s3.amazonaws.com/UAT/spacegame/index.html";
    if (!userId) return base;
    const paramObj = { userid: userId, sessionId, organizationId, source };
    if (teamId) paramObj.teamId = teamId;
    const params = new URLSearchParams(paramObj);
    const separator = base.includes("?") ? "&" : "?";
    return `${base}${separator}${params.toString()}`;
  }, [userId, sessionId, organizationId, teamId, source]);

  if (!replayChecked) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader"></div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div
        style={{
          width: "100%",
          height: "calc(100vh - 97px)",
          background: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: "0 24px",
          boxSizing: "border-box",
        }}
      >
        <p style={{ margin: 0, fontSize: "16px", color: "#222" }}>
          Not compatible with mobile devices. Please open in desktop.
        </p>
      </div>
    );
  }

  return (
    <div style={{ position: "relative", width: "100%", height: "calc(100vh - 97px)" }}>
      <iframe
        ref={iframeRef}
        src={unityUrl}
        title="Space Game"
        style={{ width: "100%", height: "100%", border: "none", display: "block" }}
        allow="fullscreen; autoplay"
      />
      <button
        type="button"
        onClick={handleFullscreenClick}
        title="Fullscreen"
        style={{
          position: "absolute",
          top: "12px",
          right: "12px",
          width: "36px",
          height: "36px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(0, 0, 0, 0.55)",
          color: "#fff",
          border: "1px solid rgba(255, 255, 255, 0.4)",
          borderRadius: "6px",
          cursor: "pointer",
          fontSize: "16px",
          lineHeight: 1,
        }}
      >
        ⛶
      </button>
    </div>
  );
}
